import PropTypes from 'prop-types'
import React, {createContext, useCallback, useContext, useMemo, useRef} from 'react'
import {PRODUCT_TILE_IMAGE_TYPE} from '../constants'
import {isAbsoluteUrl, productUrlBuilder} from '../utils/url'
import {getDisplayPrice} from '../utils/product-utils'
import {useIntl} from 'react-intl'
import useMultiSite from '../hooks/use-multi-site'
import {useCurrentCustomer} from '../hooks/use-current-customer'
import {useMutation} from '@tanstack/react-query'
import {useProductDisplayImages} from '../hooks/use-product-display-images'

const BabylistContext = createContext({enabled: false, apiLoader: async () => {}})

export const useBabylistAPILoader = () => {
    const ctx = useContext(BabylistContext)
    if (!ctx) throw new Error('useBabylistAPILoader must be used within a BabylistAPIProvider')
    return ctx
}
export const useBabylistEnabled = () => useBabylistAPILoader().enabled

export const BabylistAPIProvider = ({children, enabled = false, siteKey = 'stokke.com'}) => {
    const requestRef = useRef(null)

    const apiLoader = useCallback(() => {
        if (!enabled) return Promise.reject(new Error('BabylistAPI is not enabled'))
        if (!siteKey) return Promise.reject(new Error('BabylistAPI enabled without siteKey'))
        if (typeof window === 'undefined')
            return Promise.reject(new Error('BabylistAPI can only load in the browser'))
        if (window.bl && window.bl.addToRegistry) return Promise.resolve(window.bl)
        if (requestRef.current) return requestRef.current

        window._bl = {partner: siteKey}

        requestRef.current = new Promise((resolve, reject) => {
            const script = document.createElement('script')
            script.async = true
            script.src = 'https://www.babylist.com/add.js'
            script.onload = () => {
                if (window.bl && window.bl.addToRegistry) resolve(window.bl)
                else
                    setTimeout(() => {
                        window.bl && window.bl.addToRegistry
                            ? resolve(window.bl)
                            : reject(new Error('Failed to initialize Babylist'))
                    }, 30)
            }
            script.onerror = () => reject(new Error('Failed to load Babylist'))
            document.body.appendChild(script)
        })

        return requestRef.current
    }, [enabled, siteKey])

    const value = useMemo(() => ({enabled, apiLoader}), [enabled, apiLoader])
    return <BabylistContext.Provider value={value}>{children}</BabylistContext.Provider>
}

BabylistAPIProvider.propTypes = {
    children: PropTypes.any,
    enabled: PropTypes.bool,
    siteKey: PropTypes.string
}

export const useAddGiftToBabylist = (product) => {
    const {buildUrl} = useMultiSite()
    const {enabled, apiLoader} = useBabylistAPILoader()
    const {locale} = useIntl()
    const {isCustomNetPriceEnabled} = useCurrentCustomer().data

    const images = useProductDisplayImages(
        product,
        PRODUCT_TILE_IMAGE_TYPE,
        product.variationValues,
        1
    )

    const imageUrl = images?.[0]?.link || ''

    const gift = React.useMemo(() => {
        if (!enabled || !product) return null

        const {displayPrice} = getDisplayPrice(product, false, isCustomNetPriceEnabled)

        const _href = productUrlBuilder(product, locale)

        let _url = ''
        if (typeof window !== 'undefined') {
            _url = new URL(buildUrl(_href), window.location.origin).toString()
        }

        const url = !isAbsoluteUrl(_href) ? _url : _href

        return {
            title: product?.variant?.name || product.name,
            image: imageUrl,
            url,
            price: displayPrice
        }
    }, [enabled, product, isCustomNetPriceEnabled, locale, buildUrl, imageUrl])

    const mutation = useMutation({
        mutationFn: async (giftPayload) => {
            if (!giftPayload) throw new Error('Gift payload is empty')

            const api = await apiLoader()

            api.addToRegistry({
                title: giftPayload.title,
                price: giftPayload.price,
                image: giftPayload.image,
                url: giftPayload.url
            })

            return giftPayload
        }
    })

    return {
        enabled,
        execute: () => mutation.mutateAsync(gift),
        loading: mutation.isPending,
        error: mutation.error
    }
}
